import { useEffect } from 'react';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalization } from '@/hooks/useLocalization';
import styles from '../AuthPage.module.css';

/**
 * AccountVerifiedDialog — shown on the sign-up page once polling sees the email verified.
 *
 * Mirrors Flutter WebAccVerifiedDialog (presentation/common/web_acc_verified_dialog.dart):
 * AuthConstants.emailVerifiedSuccessMsg, "Your <brand> Account is verified successfully.",
 * and "Continue and Apply" when the user came from Apply (clickedOn == 'APPLIED'), else
 * "Continue". Like Flutter's showDialog, a tap outside (or Escape) closes it too, and the
 * caller navigates once it closes.
 */

interface AccountVerifiedDialogProps {
  showContinueApply: boolean;
  onClose: () => void;
}

export function AccountVerifiedDialog({ showContinueApply, onClose }: AccountVerifiedDialogProps) {
  const { t } = useLocalization();
  const { content } = useTenantStore();
  // Flutter Constants.brandName
  const brandName = content?.contentSettings?.general?.name ?? '';

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div
        className={`${styles.dialogContent} ${styles.dialogCentered}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-verified-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.dialogCheck} aria-hidden>✓</div>
        <h2 id="account-verified-title" className={styles.dialogTitle}>
          {t('Email Verified Successfully !')}
        </h2>
        <p className={styles.dialogMessage}>
          {`${t('Your')} ${brandName} ${t('Account is verified successfully.')}`}
        </p>
        <button type="button" className={`${styles.primaryBtn} ${styles.accentBtn} ${styles.dialogBtn}`} onClick={onClose} autoFocus>
          {showContinueApply ? t('Continue and Apply') : t('Continue')}
        </button>
      </div>
    </div>
  );
}
